/**
 * Orders and Fills column registries. Defaults keep HELM's Tradesea columns; Tradovate's Orders
 * defaults and NinjaTrader's Executions defaults ship as built-in templates
 * (docs/DESIGN-REFERENCE.md "Table columns"). Tradovate Web has no Fills module.
 */
import type { ColumnDef, TableDef } from "./columns.ts";
import {
  BRACKET_ROLE,
  ORDER_STATE_LABEL,
  ORDER_TYPE_LABEL,
  ORIGIN_BRACKET,
  SIDE_LABEL,
  TIF_LABEL,
} from "./labels.ts";
import { PENDING_FEES } from "./positions.ts";

/** The `OrderEvent` fields the Orders columns read, plus the account label. */
export type OrderFacts = {
  clientOrderId: string;
  accountId: string;
  accountLabel: string;
  symbol: string;
  side: number;
  type: number;
  qty: number;
  filled: number;
  avgFillTicks: bigint;
  limitTicks: bigint;
  stopTicks: bigint;
  tif: number;
  state: number;
  external: boolean;
  createdNs: bigint;
  updatedNs: bigint;
  rejectReason: string;
  /** Bracket links (ADR-0025): the entry's id on a child, its OCO group, role and leg. */
  parentId: string;
  ocoGroup: string;
  bracketRole: number;
  bracketLeg: number;
  origin: number;
};

/**
 * The bracket relationship in words: "Entry" for a bracketed entry; "TP1 · OCO …L1 · of …AB12" for
 * a child (its leg's OCO group and the tail of its entry's id); null for a plain order.
 */
export function bracketLabel(
  o: Pick<OrderFacts, "parentId" | "ocoGroup" | "bracketRole" | "bracketLeg">,
): string | null {
  const tail = (id: string): string => (id.length > 6 ? `…${id.slice(-6)}` : id);
  if (o.bracketRole === BRACKET_ROLE.ENTRY) return "Entry";
  const leg = String(o.bracketLeg || 1);
  const role =
    o.bracketRole === BRACKET_ROLE.TARGET
      ? `TP${leg}`
      : o.bracketRole === BRACKET_ROLE.STOP
        ? `SL${leg}`
        : null;
  if (role === null) return null;
  const parts = [role];
  if (o.ocoGroup !== "") parts.push(`OCO L${leg}`);
  if (o.parentId !== "") parts.push(`of ${tail(o.parentId)}`);
  return parts.join(" · ");
}

export type FillFacts = {
  fillId: string;
  clientOrderId: string;
  accountId: string;
  accountLabel: string;
  symbol: string;
  side: number;
  qty: number;
  priceTicks: bigint;
  tsNs: bigint;
  feeCents: bigint;
  feeIsEstimate: boolean;
  external: boolean;
};

const nsToMs = (ns: bigint): number | null => (ns === 0n ? null : Number(ns / 1_000_000n));
/** A price field where 0 means "not set" (market orders have no limit). */
const priceOrNull = (t: bigint): number | null => (t === 0n ? null : Number(t));

type OCol = ColumnDef<OrderFacts>;

export const ORDER_COLUMNS: readonly OCol[] = [
  {
    id: "time",
    label: "Time",
    category: "Order",
    definition: "When the order was created (Tradovate: Create Time; NinjaTrader: Time).",
    kind: "time",
    width: 80,
    minWidth: 64,
    value: (o) => nsToMs(o.createdNs),
  },
  {
    id: "account",
    label: "Account",
    category: "Order",
    definition: "The account the order is for.",
    kind: "account",
    width: 120,
    minWidth: 70,
    value: (o) => o.accountLabel,
  },
  {
    id: "id",
    label: "ID",
    category: "Order",
    definition: "TradePrizm's order id (the Rithmic user tag). Tradovate locks its ID column.",
    kind: "text",
    width: 110,
    minWidth: 60,
    value: (o) => o.clientOrderId,
  },
  {
    id: "symbol",
    label: "Symbol",
    category: "Order",
    definition: "The contract (Tradovate: Contract; NinjaTrader: Instrument). Always shown.",
    kind: "symbol",
    width: 80,
    minWidth: 60,
    locked: true,
    value: (o) => o.symbol,
  },
  {
    id: "action",
    label: "Order action",
    category: "Order",
    definition: "Buy or Sell.",
    kind: "side",
    width: 84,
    minWidth: 50,
    value: (o) => SIDE_LABEL[o.side] ?? null,
  },
  {
    id: "type",
    label: "Order type",
    category: "Order",
    definition: "Market, Limit, Stop (market), Stop Limit, Trailing Stop or MIT.",
    kind: "text",
    width: 84,
    minWidth: 60,
    value: (o) => ORDER_TYPE_LABEL[o.type] ?? null,
  },
  {
    id: "qty",
    label: "Qty",
    category: "Quantity",
    definition: "Order quantity (Tradovate: Order Qty).",
    kind: "qty",
    width: 56,
    minWidth: 44,
    value: (o) => o.qty,
  },
  {
    id: "filled",
    label: "Filled",
    category: "Quantity",
    definition: "Contracts filled so far (Tradovate: Filled Qty.).",
    kind: "qty",
    width: 56,
    minWidth: 44,
    value: (o) => o.filled,
  },
  {
    id: "remaining",
    label: "Remaining",
    category: "Quantity",
    definition: "Qty − Filled.",
    kind: "qty",
    width: 72,
    minWidth: 50,
    value: (o) => Math.max(0, o.qty - o.filled),
  },
  {
    id: "limit",
    label: "Limit price",
    category: "Price",
    definition: "Limit price; '—' for orders without one (Tradovate: Price).",
    kind: "price",
    width: 90,
    minWidth: 64,
    value: (o) => priceOrNull(o.limitTicks),
  },
  {
    id: "stop",
    label: "Stop price",
    category: "Price",
    definition: "Stop (trigger) price; '—' for orders without one.",
    kind: "price",
    width: 90,
    minWidth: 64,
    value: (o) => priceOrNull(o.stopTicks),
  },
  {
    id: "avgFill",
    label: "Execute price",
    category: "Price",
    definition: "Average fill price of the filled part (Tradovate: Average Fill Price).",
    kind: "price",
    width: 90,
    minWidth: 64,
    value: (o) => (o.filled > 0 ? priceOrNull(o.avgFillTicks) : null),
  },
  {
    id: "bracket",
    label: "Bracket",
    category: "Order",
    definition:
      "Bracket relationship (ADR-0025): Entry, or a child — TP/SL of leg n, its OCO group and its entry. Children are placed and kept sized by the gateway (AUTO).",
    kind: "text",
    width: 150,
    minWidth: 70,
    value: (o) => {
      const b = bracketLabel(o);
      return b === null ? null : o.origin === ORIGIN_BRACKET ? `${b} · auto` : b;
    },
  },
  {
    id: "tif",
    label: "TIF",
    category: "Order",
    definition: "Time in force: Day, GTC, IOC or FOK.",
    kind: "text",
    width: 56,
    minWidth: 44,
    value: (o) => TIF_LABEL[o.tif] ?? null,
  },
  {
    id: "charges",
    label: "Charges",
    category: "Order",
    definition: "Commissions + exchange fees on this order's fills (SPEC §10.7).",
    kind: "money",
    width: 80,
    minWidth: 60,
    pending: PENDING_FEES,
    value: () => null,
  },
  {
    id: "status",
    label: "Status",
    category: "Order",
    definition: "Order state (Tradovate: Ord Status); '· external' marks orders placed outside TradePrizm.",
    kind: "status",
    width: 100,
    minWidth: 70,
    value: (o) => ORDER_STATE_LABEL[o.state] ?? null,
  },
  {
    id: "updated",
    label: "Updated",
    category: "Order",
    definition: "Time of the order's last state change.",
    kind: "time",
    width: 80,
    minWidth: 64,
    value: (o) => nsToMs(o.updatedNs),
  },
  {
    id: "reason",
    label: "Reject reason",
    category: "Order",
    definition: "Why the order was rejected, as reported by Rithmic or TradePrizm's pre-trade checks.",
    kind: "text",
    width: 180,
    minWidth: 80,
    value: (o) => (o.rejectReason === "" ? null : o.rejectReason),
  },
  {
    id: "actions",
    label: "Actions",
    category: "Actions",
    definition: "Cancel a working order. Live on the mock / Rithmic Test only (ADR-0021).",
    kind: "action",
    width: 72,
    minWidth: 60,
    unsortable: true,
    value: () => "cancel",
  },
];

export const ORDER_DEFAULTS = [
  "time",
  "symbol",
  "action",
  "type",
  "qty",
  "limit",
  "stop",
  "avgFill",
  "bracket",
  "charges",
  "status",
  "actions",
] as const;

export const ORDERS_TABLE: TableDef<OrderFacts> = {
  id: "orders",
  label: "Orders",
  columns: ORDER_COLUMNS,
  defaults: ORDER_DEFAULTS,
  autoAccountColumn: "account",
  builtIns: [
    {
      // Tradovate Orders module: ID, Action, Order Qty, Contract, Order Type, Price, Stop Price,
      // Time in Force, Ord Status, Create Time.
      id: "builtin:tradovate",
      name: "Tradovate default",
      columns: ["id", "action", "qty", "symbol", "type", "limit", "stop", "tif", "status", "time"],
    },
    {
      // NinjaTrader 8 Orders tab (columns HELM has).
      id: "builtin:ninjatrader",
      name: "NinjaTrader default",
      columns: [
        "symbol",
        "action",
        "type",
        "qty",
        "limit",
        "stop",
        "status",
        "filled",
        "avgFill",
        "remaining",
        "tif",
        "account",
        "id",
        "time",
        "actions",
      ],
    },
  ],
};

type FCol = ColumnDef<FillFacts>;

export const FILL_COLUMNS: readonly FCol[] = [
  {
    id: "time",
    label: "Time",
    category: "Fill",
    definition: "Exchange time of the fill.",
    kind: "time",
    width: 80,
    minWidth: 64,
    value: (f) => nsToMs(f.tsNs),
  },
  {
    id: "account",
    label: "Account",
    category: "Fill",
    definition: "The account that was filled.",
    kind: "account",
    width: 120,
    minWidth: 70,
    value: (f) => f.accountLabel,
  },
  {
    id: "symbol",
    label: "Symbol",
    category: "Fill",
    definition: "The contract. Always shown.",
    kind: "symbol",
    width: 80,
    minWidth: 60,
    locked: true,
    value: (f) => f.symbol,
  },
  {
    id: "side",
    label: "Side",
    category: "Fill",
    definition: "Buy or Sell (NinjaTrader: Action).",
    kind: "side",
    width: 60,
    minWidth: 44,
    value: (f) => SIDE_LABEL[f.side] ?? null,
  },
  {
    id: "qty",
    label: "Qty",
    category: "Fill",
    definition: "Contracts filled.",
    kind: "qty",
    width: 56,
    minWidth: 44,
    value: (f) => f.qty,
  },
  {
    id: "price",
    label: "Price",
    category: "Fill",
    definition: "Fill price.",
    kind: "price",
    width: 90,
    minWidth: 64,
    value: (f) => Number(f.priceTicks),
  },
  {
    id: "fee",
    label: "Fee",
    category: "Fill",
    definition: "Commission + exchange fees on this fill (NinjaTrader: Commission). Italic = estimate.",
    kind: "money",
    width: 72,
    minWidth: 56,
    subtotal: true,
    pending: PENDING_FEES,
    value: (f) => (f.feeIsEstimate && f.feeCents === 0n ? null : f.feeCents),
  },
  {
    id: "orderId",
    label: "Order ID",
    category: "Fill",
    definition: "The order this fill belongs to.",
    kind: "text",
    width: 110,
    minWidth: 60,
    value: (f) => f.clientOrderId,
  },
  {
    id: "fillId",
    label: "ID",
    category: "Fill",
    definition: "Unique execution id.",
    kind: "text",
    width: 90,
    minWidth: 50,
    value: (f) => f.fillId,
  },
];

export const FILL_DEFAULTS = ["time", "symbol", "side", "qty", "price", "fee"] as const;

export const FILLS_TABLE: TableDef<FillFacts> = {
  id: "fills",
  label: "Fills",
  columns: FILL_COLUMNS,
  defaults: FILL_DEFAULTS,
  autoAccountColumn: "account",
  builtIns: [
    {
      // NinjaTrader 8 Executions tab, the columns HELM has (E/X, Position, Name, Rate and
      // Connection are not built).
      id: "builtin:ninjatrader",
      name: "NinjaTrader default",
      columns: ["symbol", "side", "qty", "price", "time", "fillId", "orderId", "fee", "account"],
    },
  ],
};
